import { useTu } from "@src/appV2/i18n/useTu";
import BlockIcon from "@mui/icons-material/Block";
import CheckIcon from "@mui/icons-material/Check";
import { Box, Button, Stack, type SxProps, type Theme } from "@mui/material";
import { useSession } from "@src/appV2/Auth/api/useSession";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { getUserType } from "@src/appV2/PlacementCandidates/utils/helper";
import { useCallback } from "react";
import { Text } from "../../components/Text";
import { useCancelPlacementAutoInvite } from "../api/useCancelPlacementAutoInvite";
import { useSendPlacementAutoInvite } from "../api/useSendPlacementAutoInvite";
import type { AdminChatMessage, AutoInterviewInviteData, PlacementChannelMetadata } from "../types";
import { ChatMessageCard } from "./ChatMessageCard";

interface PlacementApplicationAutoInterviewInviteProps {
  message: AdminChatMessage;
  channelUrl: string;
  channelMetadata?: PlacementChannelMetadata;
  sx?: SxProps<Theme>;
}

function parseAutoInviteData(dataString: string): AutoInterviewInviteData | undefined {
  try {
    return JSON.parse(dataString) as AutoInterviewInviteData;
  } catch {
    return undefined;
  }
}

/**
 * Renders an auto-interview invite message with send/cancel actions.
 * Shows status indicators when message is sent or cancelled.
 */
export function PlacementApplicationAutoInterviewInvite(
  props: PlacementApplicationAutoInterviewInviteProps
) {
  const { tu } = useTu("chat");

  const { message, channelUrl, channelMetadata, sx } = props;
  const { admin, profile, user } = useSession();
  const { mutate: cancelInvite, isLoading: isCancelling } = useCancelPlacementAutoInvite();
  const { mutate: sendInvite, isLoading: isSending } = useSendPlacementAutoInvite();
  const { managingFacilityId } = channelMetadata ?? {};

  // Parse the embedded data from the message
  const autoInviteData = parseAutoInviteData(message.message);

  const futureMessage = autoInviteData?.futureMessage;
  const placementApplicationId = autoInviteData?.placementApplicationId;
  const [workplaceId, workerId, placementId] = channelUrl.split("_") as Array<string | undefined>;

  const handleSendNow = useCallback(() => {
    if (!placementApplicationId || !managingFacilityId) {
      return;
    }

    logEvent(APP_V2_USER_EVENTS.PLACEMENT_CANDIDATE_CHAT_ACTION, {
      workplaceId,
      managingWorkplaceId: managingFacilityId,
      workerId,
      placementId,
      userId: user?._id,
      userType: getUserType(profile, admin),
      userEmail: user?.email,
      source: "chatBody",
      action: "interviewSendNowButtonClick",
    });

    sendInvite({
      placementApplicationId,
      workplaceId: managingFacilityId,
    });
  }, [
    placementApplicationId,
    managingFacilityId,
    workplaceId,
    workerId,
    placementId,
    user?._id,
    user?.email,
    profile,
    admin,
    sendInvite,
  ]);

  const handleDontSend = useCallback(() => {
    if (!placementApplicationId || !managingFacilityId) {
      return;
    }

    logEvent(APP_V2_USER_EVENTS.PLACEMENT_CANDIDATE_CHAT_ACTION, {
      workplaceId,
      managingWorkplaceId: managingFacilityId,
      workerId,
      placementId,
      userId: user?._id,
      userType: getUserType(profile, admin),
      userEmail: user?.email,
      source: "chatBody",
      action: "interviewDontSendButtonClick",
    });

    cancelInvite({
      placementApplicationId,
      workplaceId: managingFacilityId,
    });
  }, [
    placementApplicationId,
    managingFacilityId,
    workplaceId,
    workerId,
    placementId,
    user?._id,
    user?.email,
    profile,
    admin,
    cancelInvite,
  ]);

  // If we don't have the required data, just show as a simple card
  if (!futureMessage || !placementApplicationId || !managingFacilityId) {
    return (
      <ChatMessageCard
        subtitle={tu("Scheduled interview invite")}
        sx={sx}
        title={tu("Interview Invite")}
      />
    );
  }

  function renderStatusContent(): React.ReactNode {
    const status = futureMessage?.status;

    if (status === "queued") {
      return (
        <Stack direction="row" spacing={2}>
          <Button
            disabled={isSending || isCancelling}
            size="small"
            variant="contained"
            onClick={handleSendNow}
          >
            {tu("Send now")}
          </Button>
          <Button
            disabled={isSending || isCancelling}
            size="small"
            variant="outlined"
            onClick={handleDontSend}
          >
            {tu("Don't send")}
          </Button>
        </Stack>
      );
    }

    if (status === "cancelled") {
      return (
        <Box
          sx={(theme) => ({
            display: "flex",
            alignItems: "center",
            gap: 1,
            color: theme.palette.error.main,
          })}
        >
          <BlockIcon fontSize="small" />
          <Text variant="body2" sx={{ color: "inherit" }}>
            {tu("Message canceled")}
          </Text>
        </Box>
      );
    }

    if (status === "sent") {
      return (
        <Box
          sx={(theme) => ({
            display: "flex",
            alignItems: "center",
            gap: 1,
            color: theme.palette.success.main,
          })}
        >
          <CheckIcon fontSize="small" />
          <Text variant="body2" sx={{ color: "inherit" }}>
            {tu("Message sent")}
          </Text>
        </Box>
      );
    }

    return null;
  }

  const getSubtitle = (): string => {
    switch (futureMessage.status) {
      case "queued": {
        return tu("Scheduled interview invite pending");
      }
      case "sent": {
        return tu("Interview invite sent");
      }
      case "cancelled": {
        return tu("Interview invite canceled");
      }
      default: {
        return tu("Scheduled interview invite");
      }
    }
  };

  return (
    <ChatMessageCard
      title={tu("Interview Invite")}
      subtitle={getSubtitle()}
      caption={futureMessage.message}
      actions={renderStatusContent()}
      sx={sx}
    />
  );
}
